import React, { useEffect, useState, useMemo } from "react";
import "../../../node_modules/bootstrap/dist/css/bootstrap.min.css";
import TableContainer from "../../components/Common/TableContainer";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import DeleteModal from "../../components/Common/DeleteModal";
import {
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  Button,
  Card,
  CardBody,
} from "reactstrap";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";
import axios from "axios";

const Trash = () => {
  document.title = "Trash Overview";
  const [jobs, setJobs] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [job, setJob] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [taskDetails, setTaskDetails] = useState(null);

  const obj = JSON.parse(localStorage.getItem("authUser"));
  const userId = obj.username;

  // Function to refresh trash data
  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      const response = await axios.get(`https://api.compliance.cx/trash/${userId}`);
      setJobs(response.data.trash); // Assuming trash is returned in `data.trash`
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingData(true);
      await refreshData();
      setIsLoadingData(false);
    };

    fetchData();
  }, []);

  const onClickView = (task) => {
    setTaskDetails(task);
    setViewModal(true);
  };

  const onClickDelete = (job) => {
    setJob(job);
    setDeleteModal(true);
  };

  const handleDeletejob = async () => {
    if (job && job.id) {
      try {
        setJobs((prevJobs) => prevJobs.filter((j) => j.id !== job.id));
        setDeleteModal(false);
      } catch (error) {
        console.error("Error deleting job:", error);
      }
    }
  };

  // Handle restore job
  const handleRestoreJob = async (jobId, category) => {
    try {
      setJobs((prevJobs) => prevJobs.filter((job) => job.scanId !== jobId));
      const response = await axios.post(`https://api.compliance.cx/trash/restore/${userId}`, {
        scanId: jobId,
        category: category,
      });
      console.log("Job restored successfully:", response.data);
    } catch (error) {
      console.error("Error restoring job:", error);
      refreshData(); // Optional: sync data if needed
    }
  };

  const EmptyState = () => (
    <div className="text-center p-5">
      <div className="mb-4">
        <i className="mdi mdi-delete-empty text-danger" style={{ fontSize: "4rem" }} />
      </div>
      <h3 className="mb-3 font-weight-bold">Trash is Empty</h3>
      <p className="text-muted mb-4">
        There are no items in the trash. All your items are either active or deleted permanently.
      </p>
    </div>
  );

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "scanId",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => {
          return (
            <Link to="#" className="text-body fw-bold">
              {cellProps.row.original.scanId}
            </Link>
          );
        },
      },
      {
        header: "NAME",
        accessorKey: "assetTitle",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => {
          const row = cellProps.row.original;
          // Dynamically choose which field to display based on available keys in the row
          const assetName = row.assetName || row.assetname || row.Basic.Name;
          return <span>{assetName}</span>;
        },
      },
      {
        header: "Category",
        accessorKey: "category",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Deleted Date",
        accessorKey: "deletedDate",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => {
          const jobData = cellProps.row.original;
          const jobId = jobData.scanId;
          const category = jobData.category;

          return (
            <ul className="list-unstyled hstack gap-1 mb-0">
              <li>
                <Link
                  to="#"
                  className="btn btn-sm btn-soft-danger"
                  onClick={() => onClickDelete(jobData)}
                  id={`deletetooltip-${jobId}`}
                >
                  <i className="mdi mdi-delete-outline" />
                </Link>
              </li>
              <li>
                <Link
                  to="#"
                  className="btn btn-sm btn-soft-success"
                  onClick={() => handleRestoreJob(jobId, category)}
                  id={`restoretooltip-${jobId}`}
                >
                  <i className="mdi mdi-restore" />
                </Link>
              </li>
            </ul>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeletejob}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs breadcrumbItem="Trash" />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 card-title flex-grow-1">Trash</h5>
                    <div className="flex-shrink-0">
                      <Link
                        to="#!"
                        className="btn btn-light me-1"
                        onClick={refreshData}
                        disabled={isRefreshing}
                      >
                        <i className={`mdi mdi-refresh ${isRefreshing ? 'mdi-spin' : ''}`}></i>
                      </Link>
                    </div>
                  </div>
                </CardBody>

                <CardBody>
                  {isLoadingData ? (
                    <div className="text-center">
                      <Spinners setLoading={setIsLoadingData} />
                    </div>
                  ) : jobs.length === 0 && !isRefreshing ? (
                    <EmptyState />
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={jobs}
                      isCustomPageSize={true}
                      isGlobalFilter={true}
                      isJobListGlobalFilter={true}
                      isPagination={true}
                      SearchPlaceholder="Search for ..."
                      tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                      pagination="pagination"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Trash;


